import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { works } from "@/mocks/works";
import Breadcrumb from "@/components/base/Breadcrumb";
import StillGallery from "@/pages/works/detail/components/StillGallery";
import RelatedWorks from "@/pages/works/detail/components/RelatedWorks";
import { useFavorites } from "@/hooks/useFavorites";

export default function WorkDetailPage() {
  const { id } = useParams();
  const workId = Number(id);
  const work = works.find((item) => item.id === workId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [message, setMessage] = useState("");

  const related = useMemo(() => {
    if (!work) return [];
    const others = works.filter((item) => item.id !== work.id);
    const scored = others
      .map((item) => {
        const shared = item.genres.filter((g) => work.genres.includes(g)).length;
        const sameType = item.type === work.type ? 1 : 0;
        return { item, score: shared * 2 + sameType };
      })
      .sort((a, b) => b.score - a.score || b.item.views - a.item.views);
    return scored.slice(0, 4).map((entry) => entry.item);
  }, [work]);

  if (!work) {
    return (
      <div className="pt-24 md:pt-28">
        <div className="max-w-7xl mx-auto w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mb-5">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900">
            作品未找到，请返回作品列表
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            你访问的作品可能已被移除或链接有误。
          </p>
          <Link
            to="/works"
            className="mt-6 inline-flex items-center gap-2 px-5 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </span>
            返回作品分类页
          </Link>
        </div>
      </div>
    );
  }

  const typeLabel = work.type === "movie" ? "电影" : "电视剧";
  const favorited = isFavorite(work.id);

  const handleFavorite = () => {
    const added = toggleFavorite(work.id);
    setMessage(added ? "已添加到我的收藏" : "已取消收藏");
    window.setTimeout(() => setMessage(""), 2200);
  };

  const infoRows = [
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.join("、") },
    { label: "上映时间", value: `${work.year} 年` },
    { label: "作品类型", value: work.genres.join(" / ") },
    { label: "片长 / 集数", value: work.runtime },
  ];

  return (
    <div className="pt-24 md:pt-28">
      <div className="max-w-7xl mx-auto w-full px-4 md:px-8 py-8 md:py-12">
        <Breadcrumb
          items={[{ label: "首页", to: "/" }, { label: "作品分类", to: "/works" }, { label: work.title }]}
        />

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-8 lg:gap-12">
          <div className="w-full max-w-[360px] mx-auto lg:mx-0">
            <div className="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200 border border-background-200">
              <img
                src={work.poster}
                alt={`${work.title} 海报`}
                title={`${work.title} - 叶山小百合影视作品`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <button
              type="button"
              onClick={handleFavorite}
              className={[
                "mt-4 w-full h-11 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap",
                favorited
                  ? "bg-accent-500 text-accent-950 hover:bg-accent-400"
                  : "bg-background-100 border border-background-200 text-foreground-800 hover:border-accent-400 hover:text-accent-700",
              ].join(" ")}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
              </span>
              {favorited ? "已收藏此作品" : "收藏此作品"}
            </button>
            {message && <p className="mt-2 text-xs text-center text-accent-700">{message}</p>}
          </div>

          <div>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                {typeLabel}
              </span>
              {work.rating > 0 ? (
                <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-xs font-medium">
                  评分 {work.rating.toFixed(1)}
                </span>
              ) : (
                <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium">
                  即将上映
                </span>
              )}
              <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line text-xs"></i>
                </span>
                {work.views.toLocaleString()} 次浏览
              </span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950 leading-tight">
              《{work.title}》
            </h1>
            <p className="mt-2 text-sm text-foreground-500 tracking-wide">{work.originalTitle}</p>
            <p className="mt-4 text-base md:text-lg text-foreground-800 leading-relaxed">
              {work.tagline}
            </p>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-2 text-sm">
                    <dt className="text-foreground-500 shrink-0 w-20">{row.label}</dt>
                    <dd className="text-foreground-900">{row.value}</dd>
                  </div>
                ))}
                <div className="flex gap-2 text-sm sm:col-span-2">
                  <dt className="text-foreground-500 shrink-0 w-20">角色</dt>
                  <dd className="text-foreground-900">{work.role}</dd>
                </div>
              </dl>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to="/works"
                className="inline-flex items-center gap-2 px-5 h-11 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </span>
                返回作品分类页
              </Link>
            </div>
          </div>
        </div>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-4">
            作品简介
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-loose">{work.description}</p>
        </section>

        <section className="mt-10 md:mt-12">
          <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-4">
            剧情摘要
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-loose">{work.synopsis}</p>
        </section>

        <section className="mt-10 md:mt-14">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950">
              剧照展示
            </h2>
            <span className="text-xs text-foreground-500 flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-drag-move-2-line text-xs"></i>
              </span>
              左右滑动浏览，点击放大
            </span>
          </div>
          <StillGallery stills={work.stills} title={work.title} />
        </section>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-6">
            相关推荐
          </h2>
          <RelatedWorks works={related} />
        </section>
      </div>
    </div>
  );
}